import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import {
  CarePlanStatus,
  CarePlanListItem,
  CarePlanListResponse,
  CarePlanDetail,
  CarePlanDiscipline,
  ApiCarePlanDiscipline
} from '../../dtos/patientmr/careplans';
import type { CarePlanCertificationPeriod } from '../../dtos/patientmr/careplans';

// Re-export types for backward compatibility
export {
  CarePlanStatus,
  type CarePlanListItem,
  type CarePlanListResponse,
  type CarePlanDetail,
  type CarePlanCertificationPeriod
};

// Re-export discipline enums from DTO (single source of truth)
export { CarePlanDiscipline, ApiCarePlanDiscipline };

/**
 * Utility function to extract provider name from provider object or string
 * @param provider - The provider data (string or object)
 * @returns Formatted provider name string
 */
function extractProviderName(provider: any): string {
  if (typeof provider === 'string') {
    return provider;
  }

  if (provider && typeof provider === 'object') {
    // Try different combinations of name fields
    if (provider.firstName && provider.lastName) {
      return `${provider.firstName} ${provider.lastName}`;
    }
    if (provider.name) {
      return provider.name;
    }
    if (provider.email) {
      return provider.email;
    }
    if (provider.displayName) {
      return provider.displayName;
    }
  }

  return 'Unknown Provider';
}

/**
 * Service for managing patient care plans with backend API
 */
export class CarePlansService {

  /**
   * Get all care plans for a patient, optionally filtered by visit
   * @param patientId - The ID of the patient
   * @param visitId - Optional visit ID to filter care plans
   * @param queryParams - Optional query parameters (limit, paginationToken)
   * @returns Promise with the list of care plans and pagination metadata
   */
  static async getCarePlans(
    patientId: string,
    visitId?: string,
    queryParams: Record<string, string> = {}
  ): Promise<{ carePlans: CarePlanListItem[], hasMore: boolean, paginationToken?: string }> {
    try {
      // Build query parameters
      const params: Record<string, string> = { ...queryParams };
      if (visitId) {
        params.visitId = visitId;
      }

      const response: ApiResponse<CarePlanListResponse> = await apiClient.get(
        `/patientmr/${patientId}/careplan`,
        params
      );

      if (response.success && response.data) {
        const carePlans = response.data.careplans || [];
        const paginationInfo = response.data.paginationInfo;

        return {
          carePlans: this.formatCarePlanList(carePlans),
          hasMore: paginationInfo?.hasMoreItems || false,
          paginationToken: paginationInfo?.nextToken
        };
      }

      throw new Error(response.error?.message || 'Failed to fetch care plans');
    } catch (error) {
      console.error(`Error fetching care plans for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific care plan's details
   * @param patientId - The ID of the patient
   * @param carePlanId - The ID of the care plan
   * @returns Promise with the care plan details
   */
  static async getCarePlanDetails(patientId: string, carePlanId: string, version?: number): Promise<CarePlanDetail> {
    try {
      // `?version=n` returns a read-only snapshot of an earlier version (Phase 4)
      const response: ApiResponse<CarePlanDetail> = await apiClient.get(
        `/patientmr/${patientId}/careplan/${carePlanId}`,
        version !== undefined ? { version } : undefined
      );

      if (response.success && response.data) {
        return this.formatCarePlan(response.data);
      }

      throw new Error(response.error?.message || 'Failed to fetch care plan details');
    } catch (error) {
      console.error(`Error fetching care plan details for patient ${patientId}, care plan ${carePlanId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new care plan
   * @param patientId - The ID of the patient
   * @param visitId - The ID of the visit
   * @param carePlanData - The care plan data to create
   * @returns Promise with the created care plan
   */
  static async createCarePlan(
    patientId: string,
    visitId: string,
    carePlanData: CarePlanDetail,
    /** Phase 19 F11/F14: fill blank fields from the linked COMPLETE assessment; copy a prior COMPLETE plan */
    options: { prefill?: boolean; copyFromId?: string } = {}
  ): Promise<CarePlanDetail & { prefillSources?: Record<string, string> }> {
    try {
      // Strip formConfig.assessmentType — UI-only field, never stored in DynamoDB
      const cleanData = { ...carePlanData.data };
      if (cleanData.formConfig?.assessmentType !== undefined) {
        cleanData.formConfig = { ...cleanData.formConfig };
        delete cleanData.formConfig.assessmentType;
      }
      // Convert UI discipline to API discipline before sending to API
      const apiData = {
        ...carePlanData,
        data: cleanData,
        discipline: this.mapUiDisciplineToApiDiscipline(carePlanData.discipline),
        ...(options.prefill ? { prefill: true } : {}),
        ...(options.copyFromId ? { copyFromId: options.copyFromId } : {})
      };

      const response: ApiResponse<CarePlanDetail & { prefillSources?: Record<string, string> }> = await apiClient.post(
        `/patientmr/${patientId}/visits/${visitId}/careplan`,
        apiData
      );

      if (response.success && response.data) {
        return { ...this.formatCarePlan(response.data), prefillSources: response.data.prefillSources };
      }

      throw new Error(response.error?.message || 'Failed to create care plan');
    } catch (error) {
      console.error(`Error creating care plan for patient ${patientId}, visit ${visitId}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing care plan
   * @param patientId - The ID of the patient
   * @param carePlanId - The ID of the care plan
   * @param carePlanData - The care plan data to update
   * @returns Promise with the updated care plan
   */
  static async updateCarePlan(
    patientId: string,
    carePlanId: string,
    carePlanData: CarePlanDetail
  ): Promise<CarePlanDetail> {
    try {
      // Strip formConfig.assessmentType — UI-only field, never stored in DynamoDB
      const cleanData = { ...carePlanData.data };
      if (cleanData.formConfig?.assessmentType !== undefined) {
        cleanData.formConfig = { ...cleanData.formConfig };
        delete cleanData.formConfig.assessmentType;
      }
      // Convert UI discipline to API discipline before sending to API
      const apiData = {
        ...carePlanData,
        data: cleanData,
        discipline: this.mapUiDisciplineToApiDiscipline(carePlanData.discipline)
      };

      const response: ApiResponse<CarePlanDetail> = await apiClient.put(
        `/patientmr/${patientId}/careplan/${carePlanId}`,
        apiData
      );

      if (response.success && response.data) {
        return this.formatCarePlan(response.data);
      }

      throw new Error(response.error?.message || 'Failed to update care plan');
    } catch (error) {
      console.error(`Error updating care plan ${carePlanId} for patient ${patientId}:`, error);
      throw error;
    }
  }


  /**
   * Map form data to backend care plan data structure
   * @param formData - The form data from the UI
   * @param discipline - The discipline for the care plan
   * @returns Mapped care plan data for the backend
   */
  static mapFormDataToCarePlan(
    formData: Record<string, any>,
    discipline: CarePlanDiscipline,
    provider: any
  ): CarePlanDetail {
    // Ensure provider is a string
    const providerName = extractProviderName(provider);

    // Strip root-level fields that must not be stored inside data:
    // - status: managed at root level (DRAFT/SUBMITTED/QA/COMPLETE), not a form data field
    // - discipline: managed at root level, not a form data field
    // - formConfig.assessmentType: UI-only, stripped here and in the API call
    const { status: _status, discipline: _discipline, ...cleanFormData } = formData;
    if (cleanFormData.formConfig?.assessmentType !== undefined) {
      cleanFormData.formConfig = { ...cleanFormData.formConfig };
      delete cleanFormData.formConfig.assessmentType;
    }

    return {
      provider: providerName,
      discipline,
      data: cleanFormData
    };
  }

  /**
   * Map backend care plan data to form data structure
   * @param carePlanDetail - The care plan detail from the backend
   * @returns Mapped form data for the UI
   */
  static mapCarePlanToFormData(carePlanDetail: CarePlanDetail): Record<string, any> {
    return {
      ...carePlanDetail.data,
      provider: carePlanDetail.provider,
      discipline: carePlanDetail.discipline,
      status: carePlanDetail.status
    };
  }

  /**
   * Map UI discipline to API discipline
   * @param uiDiscipline - The UI-facing discipline enum value
   * @returns API-facing discipline enum value
   */
  static mapUiDisciplineToApiDiscipline(uiDiscipline: CarePlanDiscipline): ApiCarePlanDiscipline {
    switch (uiDiscipline) {
      case CarePlanDiscipline.RN:
        return ApiCarePlanDiscipline.RN;
      case CarePlanDiscipline.PT:
        return ApiCarePlanDiscipline.PT;
      case CarePlanDiscipline.OT:
        return ApiCarePlanDiscipline.OT;
      case CarePlanDiscipline.ST:
        return ApiCarePlanDiscipline.ST;
      case CarePlanDiscipline.MSW:
        return ApiCarePlanDiscipline.MSW;
      case CarePlanDiscipline.HHA:
        return ApiCarePlanDiscipline.HHA;
      default:
        return ApiCarePlanDiscipline.RN; // Default fallback
    }
  }

  /**
   * Map API discipline to UI discipline
   * @param apiDiscipline - The API-facing discipline enum value
   * @returns UI-facing discipline enum value
   */
  static mapApiDisciplineToUiDiscipline(apiDiscipline: ApiCarePlanDiscipline | string): CarePlanDiscipline {
    // Handle string values from API
    const disciplineStr = typeof apiDiscipline === 'string' ? apiDiscipline : String(apiDiscipline);

    switch (disciplineStr) {
      case 'RN':
      case ApiCarePlanDiscipline.RN:
        return CarePlanDiscipline.RN;
      case 'PT':
      case 'PHYSICAL_THERAPY':
      case ApiCarePlanDiscipline.PT:
        return CarePlanDiscipline.PT;
      case 'OT':
      case 'OCCUPATIONAL_THERAPY':
      case ApiCarePlanDiscipline.OT:
        return CarePlanDiscipline.OT;
      case 'ST':
      case 'SPEECH_THERAPY':
      case ApiCarePlanDiscipline.ST:
        return CarePlanDiscipline.ST;
      case 'MSW':
      case ApiCarePlanDiscipline.MSW:
        return CarePlanDiscipline.MSW;
      case 'HHA':
      case ApiCarePlanDiscipline.HHA:
        return CarePlanDiscipline.HHA;
      default:
        return CarePlanDiscipline.RN; // Default fallback
    }
  }

  /**
   * Helper function to format care plan data from the API response
   * @param carePlan Care plan data from the API
   * @returns Formatted CarePlanDetail object
   */
  static formatCarePlan(carePlan: any): CarePlanDetail {
    // Map API discipline to UI discipline
    const apiDiscipline = carePlan.discipline as ApiCarePlanDiscipline;
    const uiDiscipline = this.mapApiDisciplineToUiDiscipline(apiDiscipline);

    // Try different possible ID field names that might be returned from the API
    const carePlanId = carePlan.carePlanId || carePlan.id || carePlan.careplanId || carePlan.planId;

    const data = { ...(carePlan.data || {}) };

    // Reconstruct formConfig.assessmentType from root careplanType for UI form logic.
    // formConfig.assessmentType is UI-only and never stored in DynamoDB.
    const careplanType = carePlan.careplanType;
    if (careplanType) {
      const uiTypeMap: Record<string, string> = {
        'INITIAL': 'initial',
        'FOLLOW_UP': 'recertification',
        'DISCHARGE': 'discharge'
      };
      const frontendType = uiTypeMap[careplanType];
      if (frontendType) {
        data.formConfig = { ...data.formConfig, assessmentType: frontendType };
      }
    }

    return {
      carePlanId,
      careplanType,
      provider: extractProviderName(carePlan.provider),
      discipline: uiDiscipline,
      status: carePlan.status || CarePlanStatus.DRAFT,
      data,
      version: typeof carePlan.version === 'number' ? carePlan.version : undefined,
      reopenHistory: Array.isArray(carePlan.reopenHistory) ? carePlan.reopenHistory : undefined,
      certificationPeriodId: carePlan.certificationPeriodId ?? null,
      certificationPeriod: this.formatCertificationPeriod(carePlan.certificationPeriod)
    };
  }

  /** Phase 19 F26: the plan's `certificationPeriod` ({ startDate, endDate }), or null when absent */
  static formatCertificationPeriod(value: unknown): CarePlanCertificationPeriod | null {
    if (!value || typeof value !== 'object') return null;
    const { startDate, endDate } = value as { startDate?: unknown; endDate?: unknown };
    const day = (v: unknown): string | null => (typeof v === 'string' && v ? v.slice(0, 10) : null);
    const period = { startDate: day(startDate), endDate: day(endDate) };
    return period.startDate || period.endDate ? period : null;
  }

  /**
   * Helper function to format an array of care plans from the API
   * @param carePlans Array of care plans from the API
   * @returns Array of formatted CarePlanListItem objects
   */
  static formatCarePlanList(carePlans: any[]): CarePlanListItem[] {
    return carePlans.map(carePlan => {

      // Map API discipline to UI discipline
      const apiDiscipline = carePlan.discipline as ApiCarePlanDiscipline;
      const uiDiscipline = this.mapApiDisciplineToUiDiscipline(apiDiscipline);

      // Ensure providerName is a string - handle cases where provider might be an object
      const providerName = extractProviderName(carePlan.provider);

      // Try different possible ID field names that might be returned from the API
      const carePlanId = carePlan.carePlanId || carePlan.id || carePlan.careplanId || carePlan.planId || '';

      if (!carePlanId) {
        console.warn('Care plan is missing ID field:', carePlan);
      }

      return {
        carePlanId: carePlanId,
        date: carePlan.date || new Date().toLocaleString(),
        status: carePlan.status || CarePlanStatus.DRAFT,
        discipline: uiDiscipline,
        providerName: providerName
      };
    });
  }

  /**
   * Finalize a care plan
   * @param patientId The patient ID
   * @param careplanId The care plan ID to finalize
   * @returns A Promise that resolves when the care plan is finalized
   */
  static async finalizeCarePlan(patientId: string, careplanId: string): Promise<void> {
    try {
      const response: ApiResponse<void> = await apiClient.put(
        `/patientmr/${patientId}/careplan/${careplanId}/finalize`
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to finalize care plan');
      }
    } catch (error) {
      console.error(`Error finalizing care plan ${careplanId} for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Phase 19 F16: printable care plan (optionally a past version); returns a 5-minute link.
   */
  static async getCarePlanPdfUrl(patientId: string, careplanId: string, version?: number): Promise<{ url: string; fileName: string }> {
    const query = version ? `?version=${version}` : '';
    const response: ApiResponse<{ url: string; fileName: string }> = await apiClient.get(`/patientmr/${patientId}/careplan/${careplanId}/pdf${query}`);
    if (response.success && response.data?.url) return response.data;
    throw new Error(response.error?.message || 'Failed to prepare the care plan PDF');
  }

  /**
   * Submit a care plan
   * @param patientId The patient ID
   * @param careplanId The care plan ID to submit
   * @returns A Promise that resolves when the care plan is submitted
   */
  static async submitCarePlan(patientId: string, careplanId: string): Promise<void> {
    try {
      const response: ApiResponse<void> = await apiClient.put(
        `/patientmr/${patientId}/careplan/${careplanId}/submit`
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to submit care plan');
      }
    } catch (error) {
      console.error(`Error submitting care plan ${careplanId} for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Reopen a care plan (SUBMITTED/QA/COMPLETE -> DRAFT). The server requires a reason of at
   * least 5 characters, records it in `reopenHistory` and bumps `version`.
   * @param patientId The patient ID
   * @param careplanId The care plan ID to reopen
   * @param reason Why the care plan is being reopened or rejected
   * @returns A Promise that resolves when the care plan is reopened
   */
  static async reopenCarePlan(patientId: string, careplanId: string, reason: string): Promise<void> {
    try {
      const response: ApiResponse<void> = await apiClient.put(
        `/patientmr/${patientId}/careplan/${careplanId}/reopen`,
        { reason }
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to reopen care plan');
      }
    } catch (error) {
      console.error(`Error reopening care plan ${careplanId} for patient ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Start QA review of a submitted care plan (SUBMITTED -> QA). The reviewer comes from the JWT;
   * the author cannot start QA on their own care plan (409 SELF_APPROVAL).
   * @param patientId The patient ID
   * @param careplanId The care plan ID
   */
  static async startQACarePlan(patientId: string, careplanId: string): Promise<void> {
    try {
      const response: ApiResponse<void> = await apiClient.put(
        `/patientmr/${patientId}/careplan/${careplanId}/startQA`
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to start QA for care plan');
      }
    } catch (error) {
      console.error(`Error starting QA for care plan ${careplanId} for patient ${patientId}:`, error);
      throw error;
    }
  }
}
